/**
 * Ceasul de procesor si benzile de LED-uri ale panoului Live.
 *
 * In portal erau doua biblioteci: AmCharts (`type: "gauge"`, `easeedesk.js:6504`)
 * pentru ceas, si FusionCharts (`vled` / `hled`, `easeedesk.js:6664`) pentru
 * benzi. Aici sunt desenate cu SVG, cum se deseneaza tot ce e desenat in v4
 * (vezi `DeviceDiskManagement`, `AgentMonitorChart`): doua biblioteci de grafice
 * aduse pentru un singur panou ar fi mai grele decat panoul.
 *
 * Unghiurile, culorile si pragurile sunt CELE DIN PORTAL, ca acelasi procent sa
 * aiba aceeasi culoare pe amandoua ecranele.
 */

/** O treapta de culoare, ca `colorRange` din FusionCharts. */
export interface LedRange {
  upTo: number
  color: string
}

/** RAM: verde pana la 45, galben pana la 75, rosu peste (`easeedesk.js:6612`). */
export const RAM_RANGES: LedRange[] = [
  { upTo: 45, color: '#00e44c' },
  { upTo: 75, color: '#ffb400' },
  { upTo: 100, color: '#ca0011' },
]

/** SWAP: orice peste 10% e rosu - swapul folosit inseamna ca RAM-ul s-a terminat. */
export const SWAP_RANGES: LedRange[] = [
  { upTo: 10, color: '#00e44c' },
  { upTo: 100, color: '#ca0011' },
]

/** Citirile si scrierile de disc: o singura culoare, albastru. */
export const DISK_RANGES: LedRange[] = [{ upTo: 100, color: '#286ce4' }]

/** Reteaua: primit verde, trimis rosu, ca in portal. */
export const IN_RANGES: LedRange[] = [{ upTo: 100, color: '#00e44c' }]
export const OUT_RANGES: LedRange[] = [{ upTo: 100, color: '#ca0011' }]

function colorFor(value: number, ranges: LedRange[]): string {
  for (const range of ranges) {
    if (value <= range.upTo) {
      return range.color
    }
  }

  return ranges[ranges.length - 1]?.color ?? '#ca0011'
}

const OFF = '#DDDDDD'

/**
 * O banda de LED-uri, verticala sau orizontala.
 *
 * `value` e in procente. Peste 100 se opreste la 100 - ca in portal, unde
 * citirile de disc erau taiate cu `> 100 ? 100 : ...` inainte sa ajunga la
 * grafic (`easeedesk.js:6949`).
 */
export function LedBar({
  value,
  ranges,
  vertical = false,
  leds = 20,
  label,
}: {
  value: number
  ranges: LedRange[]
  vertical?: boolean
  leds?: number
  label?: string
}) {
  const safe = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  const on = Math.round((safe / 100) * leds)
  const color = colorFor(safe, ranges)
  const cells = Array.from({ length: leds }, (_, i) => i)

  return (
    <div className={vertical ? 'dl-led dl-led-v' : 'dl-led dl-led-h'}>
      <div className="dl-led-cells">
        {(vertical ? [...cells].reverse() : cells).map((i) => (
          <span
            key={i}
            className="dl-led-cell"
            style={{ background: i < on ? color : OFF }}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="dl-led-value">{label ?? `${Math.round(safe)}%`}</span>
    </div>
  )
}

/* Ceasul: -140 grade la stanga, +140 la dreapta, ca `startAngle`/`endAngle`
   din AmCharts. Zero e in sus. */
const START = -140
const SWEEP = 280
const SIZE = 150
const C = SIZE / 2
const R_OUT = 70
const R_IN = 35

function pointAt(value: number, radius: number): [number, number] {
  const angle = ((START + (Math.max(0, Math.min(100, value)) / 100) * SWEEP) * Math.PI) / 180

  return [C + radius * Math.sin(angle), C - radius * Math.cos(angle)]
}

function band(from: number, to: number): string {
  const [x1, y1] = pointAt(from, (R_OUT + R_IN) / 2)
  const [x2, y2] = pointAt(to, (R_OUT + R_IN) / 2)
  const large = ((to - from) / 100) * SWEEP > 180 ? 1 : 0

  return `M ${x1} ${y1} A ${(R_OUT + R_IN) / 2} ${(R_OUT + R_IN) / 2} 0 ${large} 1 ${x2} ${y2}`
}

/**
 * Ceasul de incarcare a procesorului.
 *
 * Benzile sunt cele din portal: albastru pana la 60, portocaliu pana la 80,
 * rosu peste. Cifra de sub ac se scrie cu procent, ca `bottomText`.
 */
export function CpuGauge({ value }: { value: number }) {
  const safe = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  const [nx, ny] = pointAt(safe, R_OUT * 0.8)

  return (
    <svg
      className="dl-gauge"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width={SIZE}
      height={SIZE}
      role="img"
      aria-label={`Processor load ${Math.round(safe)} percent`}
    >
      <path d={band(0, 60)} fill="none" stroke="#0084e0" strokeOpacity={0.53} strokeWidth={R_OUT - R_IN} />
      <path d={band(60, 80)} fill="none" stroke="#dc6900" strokeOpacity={0.7} strokeWidth={R_OUT - R_IN} />
      <path d={band(80, 100)} fill="none" stroke="#ea3838" strokeWidth={R_OUT - R_IN} />
      <line x1={C} y1={C} x2={nx} y2={ny} stroke="#292d35" strokeWidth={4} strokeLinecap="round" />
      <circle cx={C} cy={C} r={5} fill="#000" />
      <text x={C} y={SIZE - 8} textAnchor="middle" fontSize={18} fill="#57636f" fontFamily="Quicksand, sans-serif">
        {Math.round(safe)} %
      </text>
    </svg>
  )
}
